import { DEFAULT_LOCALE, LOCALES, Locale } from '@infiltre/shared';
import type { UiSlice } from '@/shared/lib/zustand-types';

export type LocaleSlice = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const STORAGE_KEY = 'infiltre:locale:v1';

const isLocale = (raw: string): raw is Locale => (LOCALES as readonly string[]).includes(raw);

const browserLocaleGuess = (): Locale => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const lang = navigator.language?.toLowerCase() ?? '';
  if (lang.startsWith('fr')) return Locale.FR;
  if (lang.startsWith('es')) return Locale.ES;
  if (lang.startsWith('de')) return Locale.DE;
  if (lang.startsWith('pt')) return Locale.PT;
  if (lang.startsWith('en')) return Locale.EN;
  return DEFAULT_LOCALE;
};

const loadInitialLocale = (): Locale => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && isLocale(raw)) return raw;
  } catch {
    // localStorage unavailable (private mode, SSR, etc.) — fall through.
  }
  return browserLocaleGuess();
};

export const createLocaleSlice: UiSlice<LocaleSlice> = (set) => ({
  locale: loadInitialLocale(),
  setLocale: (locale) =>
    set((s) => {
      s.locale = locale;
      try {
        localStorage.setItem(STORAGE_KEY, locale);
      } catch {
        // Persistence is best-effort; ignore quota / private-mode failures.
      }
    }),
});
